import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AttendanceCalendarCell, AttendanceCalendarResponse } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type Kind = AttendanceCalendarCell['kind'];

/**
 * Only states worth interrupting the reader for. Present is the expected case
 * and stays silent — a calendar that marks every normal day teaches you to stop
 * reading it.
 */
const NOTABLE: Record<
  Exclude<Kind, 'present' | 'weekend' | 'outside_employment' | 'working'>,
  { rail: string; text: string; word: string }
> = {
  absent: { rail: 'bg-rose-500', text: 'text-rose-700', word: 'absent' },
  lop: { rail: 'bg-orange-500', text: 'text-orange-700', word: 'loss of pay' },
  excused: { rail: 'bg-sky-500', text: 'text-sky-700', word: 'excused' },
  leave_paid: { rail: 'bg-violet-500', text: 'text-violet-700', word: 'on leave' },
  leave_unpaid: { rail: 'bg-violet-400', text: 'text-violet-700', word: 'unpaid leave' },
  leave_half: { rail: 'bg-violet-400', text: 'text-violet-700', word: 'half day' },
  leave_pending: { rail: 'bg-amber-400', text: 'text-amber-700', word: 'leave requested' },
  holiday: { rail: 'bg-[hsl(var(--brand))]', text: 'text-[hsl(var(--brand))]', word: 'holiday' },
};

type Notable = keyof typeof NOTABLE;

function isNotable(kind: Kind): kind is Notable {
  return kind in NOTABLE;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Monday-first column index, since the working week is what the grid is about. */
function columnOf(iso: string): number {
  return (new Date(`${iso}T00:00:00.000Z`).getUTCDay() + 6) % 7;
}

interface DaySummary {
  date: string;
  dayNumber: number;
  isWeekend: boolean;
  holiday: string | null;
  entries: { name: string; kind: Notable; label: string | null }[];
  presentCount: number;
  employedCount: number;
}

function summarise(data: AttendanceCalendarResponse): DaySummary[] {
  const holidayByDate = new Map(data.holidays.map((h) => [h.date, h.name]));

  return data.dates.map((date) => {
    const entries: DaySummary['entries'] = [];
    let presentCount = 0;
    let employedCount = 0;

    for (const person of data.people) {
      const cell = person.days.find((d) => d.date === date);
      if (!cell || cell.kind === 'outside_employment') continue;
      employedCount += 1;
      if (cell.kind === 'present') presentCount += 1;
      // The holiday is stated once for the whole day, not once per person.
      if (isNotable(cell.kind) && cell.kind !== 'holiday') {
        entries.push({
          name: person.personName ?? person.personId,
          kind: cell.kind,
          label: cell.label,
        });
      }
    }

    entries.sort((a, b) => a.name.localeCompare(b.name));
    const col = columnOf(date);

    return {
      date,
      dayNumber: Number(date.slice(8, 10)),
      isWeekend: col >= 5,
      holiday: holidayByDate.get(date) ?? null,
      entries,
      presentCount,
      employedCount,
    };
  });
}

export function AttendanceCalendar({ month }: { month: string }): React.ReactElement {
  const calendarQuery = useQuery({
    queryKey: ['attendance', 'calendar', month],
    queryFn: () => api.getAttendanceCalendar(month),
  });

  const data = calendarQuery.data;
  const days = React.useMemo(() => (data ? summarise(data) : []), [data]);

  const outCount = React.useMemo(
    () => days.reduce((n, d) => n + d.entries.length, 0),
    [days],
  );

  if (calendarQuery.isPending) {
    return (
      <p className="py-16 text-center text-sm text-[hsl(var(--muted-foreground))]">
        Loading the month…
      </p>
    );
  }
  if (!data || data.people.length === 0) {
    return (
      <p className="py-16 text-center text-sm text-[hsl(var(--muted-foreground))]">
        Nobody was employed during this month.
      </p>
    );
  }

  const leadingBlanks = days.length > 0 ? columnOf(days[0]!.date) : 0;
  const trailingBlanks = (7 - ((leadingBlanks + days.length) % 7)) % 7;

  return (
    <section className="flex flex-col gap-6">
      <p className="text-sm text-[hsl(var(--muted-foreground))]">
        {outCount === 0
          ? 'Nobody was away this month.'
          : `${outCount} ${outCount === 1 ? 'day' : 'days'} away across the team.`}
      </p>

      <div>
        <div className="grid grid-cols-7 border-b border-[hsl(var(--border))] pb-2">
          {WEEKDAYS.map((d, i) => (
            <div
              key={d}
              className={cn(
                'px-3 text-xs font-medium',
                i >= 5 ? 'text-[hsl(var(--muted-foreground))]/60' : 'text-[hsl(var(--muted-foreground))]',
              )}
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-px bg-[hsl(var(--border))] [&>*]:bg-[hsl(var(--background))]">
          {Array.from({ length: leadingBlanks }, (_, i) => (
            <div key={`blank-${i}`} className="min-h-28" />
          ))}
          {days.map((day) => (
            <DayCell key={day.date} day={day} />
          ))}
          {Array.from({ length: trailingBlanks }, (_, i) => (
            <div key={`tail-${i}`} className="min-h-28" />
          ))}
        </div>
      </div>
    </section>
  );
}

function DayCell({ day }: { day: DaySummary }): React.ReactElement {
  const quiet = day.isWeekend || day.holiday !== null;

  return (
    <div
      className={cn(
        'flex min-h-28 flex-col gap-1.5 p-3',
        day.isWeekend && 'bg-[hsl(var(--muted))]/40',
        day.holiday && 'bg-[hsl(var(--brand))]/[0.045]',
      )}
    >
      <div className="flex items-baseline gap-2">
        <span
          className={cn(
            'text-sm tabular-nums',
            quiet
              ? 'text-[hsl(var(--muted-foreground))]'
              : 'font-medium text-[hsl(var(--foreground))]',
          )}
        >
          {day.dayNumber}
        </span>
        {day.holiday ? (
          <span className="truncate text-xs font-medium text-[hsl(var(--brand))]">
            {day.holiday}
          </span>
        ) : null}
      </div>

      {day.entries.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {day.entries.map((entry) => (
            <li
              key={`${entry.name}:${entry.kind}`}
              title={`${entry.name} — ${NOTABLE[entry.kind].word}${
                entry.label ? ` (${entry.label})` : ''
              }`}
              className="flex gap-1.5 text-xs leading-snug"
            >
              <span
                className={cn('mt-0.5 w-0.5 shrink-0 self-stretch rounded-full', NOTABLE[entry.kind].rail)}
              />
              <span className="min-w-0">
                <span className="text-[hsl(var(--foreground))]">{firstName(entry.name)}</span>{' '}
                <span className={cn('text-[10px]', NOTABLE[entry.kind].text)}>
                  {NOTABLE[entry.kind].word}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : !quiet && day.employedCount > 0 && day.presentCount === day.employedCount ? (
        <span className="text-xs text-[hsl(var(--muted-foreground))]/60">All in</span>
      ) : null}
    </div>
  );
}

function firstName(full: string): string {
  return full.split(' ')[0] ?? full;
}
